import { useTranslation } from '../../../shared/i18n';
import React, { useState, useEffect } from 'react';
import {
    Table, Button, Space, Modal, Form, Input, Select, message, Breadcrumb,
    Upload, Dropdown, Menu, Tag, Card, Row, Col, Tooltip
} from 'antd';
import {
    FolderOutlined, FileOutlined, UploadOutlined, DownloadOutlined,
    DeleteOutlined, EditOutlined, CopyOutlined, ScissorOutlined,
    PlusOutlined, FolderAddOutlined, ReloadOutlined, HomeOutlined,
    CompressOutlined, FileZipOutlined, MoreOutlined, CloudDownloadOutlined
} from '@ant-design/icons';
import hostService from '../../../shared/services/hostService';
import { userFileService } from '../../../shared/services/fileService';
import useAsyncTask from '../../../shared/hooks/useAsyncTask';
import TaskProgress from '../../../shared/components/TaskProgress';

const { TextArea } = Input;
const { Option } = Select;

const FileManager = ({ containerId: propContainerId, embedded = false }) => {
    const { t } = useTranslation();
    const [containers, setContainers] = useState([]);
    const [selectedContainer, setSelectedContainer] = useState(propContainerId || null);
    const [currentPath, setCurrentPath] = useState('/');
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [createModalVisible, setCreateModalVisible] = useState(false);
    const [uploadModalVisible, setUploadModalVisible] = useState(false);
    const [renameModalVisible, setRenameModalVisible] = useState(false);
    const [remoteDownloadModalVisible, setRemoteDownloadModalVisible] = useState(false);
    const [currentFile, setCurrentFile] = useState(null);
    const [fileContent, setFileContent] = useState('');
    const [form] = Form.useForm();
    const [renameForm] = Form.useForm();
    const [remoteDownloadForm] = Form.useForm();

    // 异步任务管理
    const {
        tasks,
        compressAsync,
        extractAsync,
        remoteDownloadAsync,
        pollTaskStatus,
        clearCompletedTasks
    } = useAsyncTask(selectedContainer);

    useEffect(() => {
        // 如果是嵌入模式且有propContainerId，直接使用
        if (embedded && propContainerId) {
            setSelectedContainer(propContainerId);
            return;
        }

        loadContainers();

        // 从URL参数获取container_id并预选择容器
        const urlParams = new URLSearchParams(window.location.search);
        const containerIdParam = urlParams.get('container_id');
        if (containerIdParam) {
            setSelectedContainer(parseInt(containerIdParam));
        }
    }, [propContainerId, embedded]);

    useEffect(() => {
        if (selectedContainer) {
            loadFiles(currentPath);
        }
    }, [selectedContainer, currentPath]);

    const loadContainers = async () => {
        try {
            const response = await hostService.getContainers();
            if (response.success) {
                setContainers(response.data || []);
            }
        } catch (error) {
            message.error(t('Failed to retrieve containers'));
        }
    };

    const loadFiles = async (path) => {
        if (!selectedContainer) return;

        setLoading(true);
        try {
            const response = await userFileService.listFiles(selectedContainer, path);
            if (response.success) {
                setFiles(response.data?.data || []);
            } else {
                message.error(response.error || t('Failed to retrieve files'));
            }
        } catch (error) {
            message.error(t('Failed to retrieve files'));
        } finally {
            setLoading(false);
        }
    };

    const handleFileClick = (file) => {
        if (file.is_dir) {
            // 进入目录
            setCurrentPath(file.path);
        } else {
            // 打开文件编辑器
            handleEditFile(file);
        }
    };

    const handleEditFile = async (file) => {
        setLoading(true);
        try {
            const response = await userFileService.readFile(selectedContainer, file.path);
            if (response.success) {
                setCurrentFile(file);
                setFileContent(response.data?.data?.content || '');
                setEditModalVisible(true);
            } else {
                message.error(response.error || t('Failed to read the file'));
            }
        } catch (error) {
            message.error(t('Failed to read the file'));
        } finally {
            setLoading(false);
        }
    };

    const handleSaveFile = async () => {
        if (!currentFile) return;

        try {
            const response = await userFileService.writeFile(
                selectedContainer,
                currentFile.path,
                fileContent
            );
            if (response.success) {
                message.success(t('File saved'));
                setEditModalVisible(false);
                loadFiles(currentPath);
            } else {
                message.error(response.error || t('Failed to save file'));
            }
        } catch (error) {
            message.error(t('Failed to save file'));
        }
    };

    const handleCreateFile = async (values) => {
        const path = currentPath === '/' ? `/${values.name}` : `${currentPath}/${values.name}`;

        try {
            if (values.type === 'file') {
                const response = await userFileService.writeFile(selectedContainer, path, '');
                if (response.success) {
                    message.success(t('File created'));
                    setCreateModalVisible(false);
                    form.resetFields();
                    loadFiles(currentPath);
                } else {
                    message.error(response.error || t('Failed to create file'));
                }
            } else {
                const response = await userFileService.createDirectory(selectedContainer, path);
                if (response.success) {
                    message.success(t('Directory created successfully'));
                    setCreateModalVisible(false);
                    form.resetFields();
                    loadFiles(currentPath);
                } else {
                    message.error(response.error || t('Failed to create directory'));
                }
            }
        } catch (error) {
            message.error(t('Creation failed'));
        }
    };

    const handleDelete = (file) => {
        Modal.confirm({
            title: file.is_dir ? t('Delete the directory?') : t('Delete the file?'),
            content: file.is_dir ? t('{{name}} and all its contents will be deleted. This cannot be undone!', { name: file.name }) : t('{{name}} will be deleted. This cannot be undone!', { name: file.name }),
            okType: 'danger',
            onOk: async () => {
                try {
                    const response = await userFileService.deleteFile(selectedContainer, file.path);
                    if (response.success) {
                        message.success(t('Deleted successfully'));
                        loadFiles(currentPath);
                    } else {
                        message.error(response.error || t('Deletion failed'));
                    }
                } catch (error) {
                    message.error(t('Deletion failed'));
                }
            }
        });
    };

    const handleDownload = async (file) => {
        try {
            const response = await userFileService.downloadFile(selectedContainer, file.path);
            if (!response.success) {
                message.error(response.error || t('Download failed'));
            }
        } catch (error) {
            message.error(t('Download failed'));
        }
    };

    const handleUpload = async (file) => {
        try {
            const response = await userFileService.uploadFile(selectedContainer, currentPath, file);
            if (response.success) {
                message.success(t('Uploaded successfully'));
                setUploadModalVisible(false);
                loadFiles(currentPath);
            } else {
                message.error(response.error || t('Upload failed'));
            }
        } catch (error) {
            message.error(t('Upload failed'));
        }
        return false; // 阻止默认上传行为
    };

    const handleRename = async (values) => {
        if (!currentFile) return;

        const newPath = currentFile.path.substring(0, currentFile.path.lastIndexOf('/') + 1) + values.new_name;

        try {
            const response = await userFileService.moveFile(selectedContainer, currentFile.path, newPath);
            if (response.success) {
                message.success(t('Renamed successfully'));
                setRenameModalVisible(false);
                renameForm.resetFields();
                loadFiles(currentPath);
            } else {
                message.error(response.error || t('Rename failed'));
            }
        } catch (error) {
            message.error(t('Rename failed'));
        }
    };

    const handleCompress = (file) => {
        let selectedFormat = 'tar.gz';

        Modal.confirm({
            title: t('Compress files'),
            content: (
                <Form>
                    <Form.Item label={t('Compression format')}>
                        <Select
                            defaultValue="tar.gz"
                            onChange={(value) => { selectedFormat = value; }}
                        >
                            <Option value="tar.gz">tar.gz</Option>
                            <Option value="zip">zip</Option>
                        </Select>
                    </Form.Item>
                </Form>
            ),
            onOk: async () => {
                const dstPath = `${file.path}.${selectedFormat}`;
                try {
                    const result = await compressAsync(file.path, dstPath, selectedFormat);
                    if (result.success) {
                        message.info(t('Compression task started. Please check task progress.'));
                        // 开始轮询任务状态
                        pollTaskStatus(
                            result.taskId,
                            () => {
                                message.success(t('Compression complete'));
                                loadFiles(currentPath);
                            },
                            (error) => {
                                message.error(t('Compression failed: {{error}}', { error }));
                            }
                        );
                    } else {
                        message.error(result.error || t('Failed to start compression task'));
                    }
                } catch (error) {
                    message.error(t('Compression failed'));
                }
            }
        });
    };

    const handleExtract = (file) => {
        Modal.confirm({
            title: t('Extract file'),
            content: t('Extract {{name}} to the current directory?', { name: file.name }),
            onOk: async () => {
                // 解压到与压缩文件同名的目录
                const dstPath = file.path.replace(/\.(tar\.gz|zip)$/, '');
                try {
                    const result = await extractAsync(file.path, dstPath);
                    if (result.success) {
                        message.info(t('Extraction started. Please check task progress'));
                        // 开始轮询任务状态
                        pollTaskStatus(
                            result.taskId,
                            () => {
                                message.success(t('Extraction complete'));
                                loadFiles(currentPath);
                            },
                            (error) => {
                                message.error(t('Extraction failed: {{error}}', { error }));
                            }
                        );
                    } else {
                        message.error(result.error || t('Failed to start extraction task'));
                    }
                } catch (error) {
                    message.error(t('Extraction failed'));
                }
            }
        });
    };

    const handleRemoteDownload = async (values) => {
        try {
            const dstPath = currentPath === '/'
                ? `/${values.filename}`
                : `${currentPath}/${values.filename}`;

            const result = await remoteDownloadAsync(values.url, dstPath);
            if (result.success) {
                message.info(t('Download task started. Check the task progress.'));
                setRemoteDownloadModalVisible(false);
                remoteDownloadForm.resetFields();
                // 开始轮询任务状态
                pollTaskStatus(
                    result.taskId,
                    () => {
                        message.success(t('Remote download complete'));
                        loadFiles(currentPath);
                    },
                    (error) => {
                        message.error(t('Download failed: {{error}}', { error }));
                    }
                );
            } else {
                message.error(result.error || t('Failed to start download task'));
            }
        } catch (error) {
            message.error(t('Download failed'));
        }
    };

    const getBreadcrumbItems = () => {
        const parts = currentPath.split('/').filter(p => p);
        const items = [
            {
                title: (
                    <a onClick={() => setCurrentPath('/')}>
                        <HomeOutlined /> {t('Root directory')}
                    </a>
                )
            }
        ];

        let path = '';
        parts.forEach((part, index) => {
            path += '/' + part;
            const fullPath = path;
            items.push({
                title: <a onClick={() => setCurrentPath(fullPath)}>{part}</a>
            });
        });

        return items;
    };

    const getFileIcon = (file) => {
        return file.is_dir ? (
            <FolderOutlined style={{ color: '#1890ff', fontSize: 18 }} />
        ) : (
            <FileOutlined style={{ color: '#666', fontSize: 18 }} />
        );
    };

    const getFileMenu = (file) => {
        const isCompressedFile = !file.is_dir && (file.name.endsWith('.tar.gz') || file.name.endsWith('.zip'));

        return (
            <Menu>
                {!file.is_dir && (
                    <>
                        <Menu.Item key="edit" icon={<EditOutlined />} onClick={() => handleEditFile(file)}>
                            {t('Edit')}
                        </Menu.Item>
                        <Menu.Item key="download" icon={<DownloadOutlined />} onClick={() => handleDownload(file)}>
                            {t('Download')}
                        </Menu.Item>
                    </>
                )}
                <Menu.Item
                    key="rename"
                    icon={<EditOutlined />}
                    onClick={() => {
                        setCurrentFile(file);
                        renameForm.setFieldsValue({ new_name: file.name });
                        setRenameModalVisible(true);
                    }}
                >
                    {t('Rename')}
                </Menu.Item>
                <Menu.Item key="compress" icon={<CompressOutlined />} onClick={() => handleCompress(file)}>
                    {t('Compress')}
                </Menu.Item>
                {isCompressedFile && (
                    <Menu.Item key="extract" icon={<FileZipOutlined />} onClick={() => handleExtract(file)}>
                        {t('Extract')}
                    </Menu.Item>
                )}
                <Menu.Divider />
                <Menu.Item key="delete" icon={<DeleteOutlined />} danger onClick={() => handleDelete(file)}>
                    {t('Delete')}
                </Menu.Item>
            </Menu>
        );
    };

    const columns = [
        {
            title: t('Name'),
            dataIndex: 'name',
            key: 'name',
            render: (text, record) => (
                <Space>
                    {getFileIcon(record)}
                    <a onClick={() => handleFileClick(record)}>{text}</a>
                </Space>
            ),
        },
        {
            title: t('Size'),
            dataIndex: 'size',
            key: 'size',
            width: 120,
            render: (size, record) => {
                if (record.is_dir) return '-';
                if (size < 1024) return `${size} B`;
                if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} KB`;
                if (size < 1024 * 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(2)} MB`;
                return `${(size / (1024 * 1024 * 1024)).toFixed(2)} GB`;
            },
        },
        {
            title: t('Modified at'),
            dataIndex: 'mod_time',
            key: 'mod_time',
            width: 180,
        },
        {
            title: t('Permissions'),
            dataIndex: 'mode',
            key: 'mode',
            width: 120,
        },
        {
            title: t('Actions'),
            key: 'action',
            width: 80,
            fixed: 'right',
            render: (_, record) => (
                <Dropdown overlay={getFileMenu(record)} trigger={['click']}>
                    <Button size="small" icon={<MoreOutlined />} />
                </Dropdown>
            ),
        },
    ];

    return (
        <div>
            {!embedded && <h2>{t('File manager')}</h2>}
            <Card>
                <Row gutter={16} style={{ marginBottom: 16 }}>
                    {!embedded && (
                        <Col span={8}>
                            <Select
                                style={{ width: '100%' }}
                                placeholder={t('Select a container')}
                                value={selectedContainer}
                                onChange={setSelectedContainer}
                                showSearch
                                filterOption={(input, option) =>
                                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                            >
                                {containers.map(c => (
                                    <Option key={c.id} value={c.id}>
                                        {c.name} ({c.container_type})
                                    </Option>
                                ))}
                            </Select>
                        </Col>
                    )}
                    <Col span={embedded ? 24 : 16}>
                        <Space>
                            <Button
                                type="primary"
                                icon={<UploadOutlined />}
                                onClick={() => setUploadModalVisible(true)}
                                disabled={!selectedContainer}
                            >
                                {t('Upload')}
                            </Button>
                            <Button
                                icon={<CloudDownloadOutlined />}
                                onClick={() => setRemoteDownloadModalVisible(true)}
                                disabled={!selectedContainer}
                            >
                                {t('Remote download')}
                            </Button>
                            <Button
                                icon={<PlusOutlined />}
                                onClick={() => setCreateModalVisible(true)}
                                disabled={!selectedContainer}
                            >
                                {t('Create')}
                            </Button>
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={() => loadFiles(currentPath)}
                                disabled={!selectedContainer}
                            >
                                {t('Refresh')}
                            </Button>
                        </Space>
                    </Col>
                </Row>

                {/* 任务进度显示 */}
                {tasks.length > 0 && (
                    <TaskProgress
                        tasks={tasks}
                        onClearCompleted={clearCompletedTasks}
                        style={{ marginBottom: 16 }}
                    />
                )}

                {selectedContainer && (
                    <Breadcrumb items={getBreadcrumbItems()} style={{ marginBottom: 16 }} />
                )}

                <Table
                    columns={columns}
                    dataSource={files}
                    rowKey="path"
                    loading={loading}
                    pagination={false}
                    scroll={{ x: 800 }}
                />
            </Card>

            {/* 文件编辑器 */}
            <Modal
                title={t('Edit file: {{name}}', { name: currentFile?.name })}
                open={editModalVisible}
                onCancel={() => setEditModalVisible(false)}
                onOk={handleSaveFile}
                width={800}
            >
                <TextArea
                    value={fileContent}
                    onChange={(e) => setFileContent(e.target.value)}
                    rows={20}
                    style={{ fontFamily: 'monospace' }}
                />
            </Modal>

            {/* 新建文件/目录 */}
            <Modal
                title={t('Create')}
                open={createModalVisible}
                onCancel={() => {
                    setCreateModalVisible(false);
                    form.resetFields();
                }}
                onOk={() => form.submit()}
            >
                <Form form={form} layout="vertical" onFinish={handleCreateFile}>
                    <Form.Item name="type" label={t('Type')} initialValue="file" rules={[{ required: true }]}>
                        <Select>
                            <Option value="file">{t('File')}</Option>
                            <Option value="directory">{t('Directory')}</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="name" label={t('Name')} rules={[{ required: true, message: t('Please enter a name') }]}>
                        <Input placeholder={t('File or directory name')} />
                    </Form.Item>
                </Form>
            </Modal>

            {/* 上传文件 */}
            <Modal
                title={t('Upload file')}
                open={uploadModalVisible}
                onCancel={() => setUploadModalVisible(false)}
                footer={null}
            >
                <Upload.Dragger
                    beforeUpload={handleUpload}
                    showUploadList={true}
                    multiple={false}
                >
                    <p className="ant-upload-drag-icon">
                        <UploadOutlined />
                    </p>
                    <p className="ant-upload-text">{t('Click or drag files here to upload')}</p>
                    <p className="ant-upload-hint">{t('Upload to current directory: {{currentPath}}', { currentPath })}</p>
                </Upload.Dragger>
            </Modal>

            {/* 重命名 */}
            <Modal
                title={t('Rename')}
                open={renameModalVisible}
                onCancel={() => {
                    setRenameModalVisible(false);
                    renameForm.resetFields();
                }}
                onOk={() => renameForm.submit()}
            >
                <Form form={renameForm} layout="vertical" onFinish={handleRename}>
                    <Form.Item name="new_name" label={t('New name')} rules={[{ required: true, message: t('Please enter a new name') }]}>
                        <Input placeholder={t('New file or directory name')} />
                    </Form.Item>
                </Form>
            </Modal>

            {/* 远程下载 */}
            <Modal
                title={t('Download a remote file')}
                open={remoteDownloadModalVisible}
                onCancel={() => {
                    setRemoteDownloadModalVisible(false);
                    remoteDownloadForm.resetFields();
                }}
                onOk={() => remoteDownloadForm.submit()}
            >
                <Form form={remoteDownloadForm} layout="vertical" onFinish={handleRemoteDownload}>
                    <Form.Item
                        name="url"
                        label={t('Download URL')}
                        rules={[
                            { required: true, message: t('Please enter a download URL') },
                            { type: 'url', message: t('Enter a valid URL') }
                        ]}
                    >
                        <Input placeholder="https://example.com/file.zip" />
                    </Form.Item>
                    <Form.Item
                        name="filename"
                        label={t('Save filename')}
                        rules={[{ required: true, message: t('Please enter a filename') }]}
                    >
                        <Input placeholder="file.zip" />
                    </Form.Item>
                    <p style={{ color: '#999', fontSize: 12 }}>
                        {t('File will be saved to: {{path}}', { path: currentPath === '/' ? '/' : currentPath + '/' })}
                    </p>
                </Form>
            </Modal>
        </div>
    );
};

export default FileManager;
